// DO NOT REMOVE:
import { generateClient } from "aws-amplify/api";

import { useEffect, useRef, useState } from "react";
import { AppState, DeviceEventEmitter } from "react-native";

import { fetchEvent, subscriptionEventUpdated } from "../http/events";
import { safeGraphqlSubscribe } from "../http/graphqlSubscriptions";

export const client = generateClient();

const useSubscribeToEventUpdates = (initialEvent = null, fetchFirst = true) => {
  const broadcaster = initialEvent ? initialEvent.broadcaster : null;
  const slug = initialEvent ? initialEvent.slug : null;
  const [refreshEvent, setrefreshEvent] = useState(false);
  const [event, setEvent] = useState(initialEvent);
  const [error, setError] = useState(false);
  const [loading, setloading] = useState(fetchFirst);

  // The screen moved to another event in place (a room switch on phones):
  // show the copy that came with it now rather than the previous event until
  // the new one has loaded, and refresh it quietly below.
  const [shownSlug, setShownSlug] = useState(slug);
  if (slug !== shownSlug) {
    setShownSlug(slug);
    if (initialEvent?.title) setEvent(initialEvent);
  }

  const eventRef = useRef(event);
  useEffect(() => {
    eventRef.current = event;
  }, [event, slug]);

  const effectSlug = useRef(slug);

  useEffect(() => {
    let subscription;
    let retryTimer;
    // Set once this effect is replaced (another slug, a refresh) or the screen
    // closes: a late answer for the previous slug mustn't overwrite the event.
    let replaced = false;
    const switchedWithCopy =
      effectSlug.current !== slug &&
      eventRef.current?.slug === slug &&
      !!eventRef.current?.title;
    effectSlug.current = slug;

    const refetch = async (silent = false) => {
      try {
        if (!silent) setloading(true);
        // Teams: the account the event is in (the workspace's, or your own
        // for tests); changes nothing in your own account.
        const { myEvent } = await fetchEvent(slug, { owner: broadcaster });

        if (replaced) {
          if (!silent) setloading(false);
          return;
        }
        if (!myEvent) {
          setError(true);
          throw new Error("Could not load");
        }
        setEvent(myEvent);
        if (!silent) setloading(false);
      } catch (err) {
        if (!silent) setloading(false);
        return {};
      }
    };

    const subscribe = (silent = false) => {
      if (!broadcaster || !slug) {
        return;
      }

      if (subscription) {
        subscription.unsubscribe();
        refetch(silent);
      }

      subscription = safeGraphqlSubscribe({
        client,
        request: {
          query: subscriptionEventUpdated,
          variables: { broadcaster, slug },
        },
        observer: {
          next: (payload) => {
            // Filter out null values from subscription data so partial
            // backend updates don't overwrite existing fields with null.
            const update =
              payload?.data?.eventUpdated ?? payload?.value?.data?.eventUpdated;
            if (!update) return;
            const filtered = {};
            for (const key in update) {
              if (
                update[key] != null ||
                (key.startsWith("donation_") && update.donation_updated_at)
              ) {
                filtered[key] = update[key];
              }
            }
            setEvent({
              ...eventRef.current,
              ...filtered,
            });
          },
          error: (err) => {
            console.log("Unsubscribed.."); // what is the error in the subscription?
            //  subscribe(); /????  with Timeout
            retryTimer = setTimeout(subscribe, 10000); // SILENT here
          },
        },
      });
    };

    if (broadcaster && slug) {
      if (fetchFirst || (eventRef.current && eventRef.current.slug != slug)) {
        refetch(switchedWithCopy);
      }
      subscribe();
    }

    const handleStateChange = (newState) => {
      if (newState === "active") {
        subscribe(true);
      }
    };

    const changeListener = AppState.addEventListener(
      "change",
      handleStateChange
    );

    const sub = DeviceEventEmitter.addListener("refresh_event", () => {
      setrefreshEvent(!refreshEvent);
    });

    return () => {
      replaced = true;
      // unsubscribe
      if (subscription) {
        subscription.unsubscribe();
      }
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      if (changeListener) {
        changeListener.remove();
      }

      sub.remove();
    };
  }, [broadcaster, slug, refreshEvent]);

  return { event, loading, error };
};

export default useSubscribeToEventUpdates;
